import React, { useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Page from 'components/Page';
import CTTabs from 'components/CTTabs';
import * as EquipmentAction from 'actions/equipment-action';
import * as TruckAction from 'actions/truck-action';
import FileUploader from 'components/FileUploader';
import DataDialog from 'components/dialogs/DataDialog';
import EquipmentForm from './equipment-form';
import PageHeader from 'global/PageHeader';
import { Box, Button, ButtonGroup, Chip, CircularProgress, Divider, IconButton, Menu, MenuItem, Typography, useTheme } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { EditOutlined, MoreVert } from '@mui/icons-material';
import ToastHandler from 'components/Toast-Handler/toast-handler';
import Searchbar from 'global/Searchbar';
import Notificationbar from 'global/Notificationbar';

import TruckForm from './truck-form';
import MaintenanceForm from './maintenance-form';
import ContentDrawer from 'components/ContentDrawer';
import FormElements from 'components/FormElements';
import ButtonCancel from 'components/ButtonCancel';
import ButtonSave from 'components/ButtonSave';
import FTIcons from 'assets/icons/FTIcons';
import { tokens } from 'theme/tokens';
import FormDrawer from 'components/FormDrawer';

const TABS = ['TRUCK', 'CONTAINER', 'CHASSIS'];

const EquipmentPage = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const dispatch = useDispatch();
  // Redux state
  const {
    equipments,
    searched,
    isProcessing,
    toast,
  } = useSelector(state => state.equipmentReducer);

  const {
    trucks,
    searchedTrucks,
    isLoading,
    truckToast,
  } = useSelector(state => state.truckReducer);

  // Form default state
  const formState = {
    equipment: {
      id: '',
      name: '',
      type: '',
      description: '',
      height: '',
      length: '',
      street: '',
      city: '',
      state: '',
      zipcode: '',
      country: '',
      freeDays: 0,
      rent: 0,
      isActive: true,
    },
    truck: {
      truckType: '',
      mcNumber: '',
      dotNumber: '',
      truckNumber: '',
      make: '',
      model: '',
      registeredCountry: '',
      registeredState: '',
      licensePlate: '',
      registrationExpirationDate: '',
      year: '',
      vinNumber: '',
      insuranceNumber: '',
      insuranceExpirationDate: '',
      isAvailable: true,
      capacityWeight: '',
      length: '',
      totalMilesDriven: '',
      lastServiceDate: '',
      maintenanceRecordList: [],
    },
  };

  // Component state
  const [equipment, setEquipment] = useState(formState.equipment);
  const [truck, setTruck] = useState(formState.truck);
  const [action, setAction] = useState('Save');
  const [tabIndex, setTabIndex] = useState(0);
  const [isFormDialogOpen, setIsFormDialogOpen] = useState(false);
  const [isEquipmentFormDrawerOpen, setIsEquipmentFormDrawerOpen] = useState(false);
  const [isTruckFormDrawerOpen, setIsTruckFormDrawerOpen] = useState(false);
  const [isFileLoaderDialogOpen, setIsFileLoaderDialogOpen] = useState(false);
  const [isMaintenanceDialogOpen, setIsMaintenanceDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isSearchEnabled, setIsSearchEnabled] = useState(false);
  const [searchParameters, setSearchParameters] = useState({});
  const [maintenanceList, setMaintenanceList] = useState([]);
  const [maintenanceRecord, setMaintenanceRecord] = useState({
    maintenanceId: '',
    date: '',
    description: '',
    amount: '',
    serviceCenter: '',
    mileage: '',
    address: '',
    volume: '',
    category: '',
  });
  const [selectedTruck, setSelectedTruck] = useState(null);

  // To store submitForm function exposed by child form
  const submitFormRef = useRef(() => {});

  // Columns are constant; could be useMemo but static is fine here
  const columns = [
    { field: 'id', headerName: 'Id' },
    { field: 'type', headerName: 'Type', flex: 1 },
    { field: 'name', headerName: 'Name', flex: 1 },
    { field: 'freeDays', headerName: 'Free Days', flex: 1 },
    { field: 'rent', headerName: 'Rent', flex: 1 },
    { field: 'street', headerName: 'Street', flex: 1, valueGetter: (value, row) => row.address?.street },
    { field: 'city', headerName: 'City', flex: 1, valueGetter: (value, row) => row.address?.city },
    { field: 'state', headerName: 'State', flex: 1, valueGetter: (value, row) => row.address?.state },
    { field: 'zip', headerName: 'Zip', flex: 1, valueGetter: (value, row) => row.address?.zipcode },
    { field: 'country', headerName: 'Country', flex: 1, valueGetter: (value, row) => row.address?.country },
    {
      field: 'action',
      headerName: 'Action',
      flex: 1,
      renderCell: (params) => (
        <IconButton onClick={() => editEquipment(params.row)}>
          <FTIcons.EditIcon
            width={20}
            height={20}
            fill={colors.primary.main}
          />
        </IconButton>
      ),
    },
  ];

  const truckHeaders = [
    { field: 'id', headerName: 'Id' },
    { field: 'dotNumber', headerName: 'DOT' },
    {
      field: 'truckType',
      headerName: 'Type',
      flex: 1,
      renderCell: (param) => (param.row.truckType === 'LIVESTOCK' ? 'TRACTOR' : param.row.truckType),
    },
    { field: 'truckNumber', headerName: 'Unit Number', flex: 1 },
    { field: 'licensePlate', headerName: 'License', flex: 1 },
    { field: 'registrationExpirationDate', headerName: 'Registration Exp.Date', flex: 1 },
    { field: 'insuranceNumber', headerName: 'Insurance', flex: 1 },
    { field: 'insuranceExpirationDate', headerName: 'Insurance Exp.Date', flex: 1 },
    { field: 'make', headerName: 'Make', flex: 1 },
    { field: 'totalMilesDriven', headerName: 'Miles', flex: 1 },
    {
      field: 'action',
      headerName: 'Action',
      renderCell: (params) => (
        <Box>
          <IconButton onClick={() => editTruck(params.row)}>
            <FTIcons.EditIcon
              width={20}
              height={20}
              fill={colors.primary.main}
            />
          </IconButton>
          <IconButton onClick={() => addMaintenanceInformation(params.row)}>
            <FTIcons.MaintenanceIcon
              width={20}
              height={20}
              fill={colors.primary.main}
            />
          </IconButton>          
        </Box>
      ),
    },
  ];

  /* Search Handlers */
  const executeSearch = (searchParams) => {
    if (searchParams && Object.entries(searchParams).length > 0) {
      const value = searchParams['name'];
      setIsSearchEnabled(true);
      setSearchParameters(searchParams);
      dispatch(EquipmentAction.search(value));
    } else {
      setIsSearchEnabled(false);
      setSearchParameters({});
    }
  };

  const createSearchChip = () => {
    const kvpair = Object.entries(searchParameters).map(([k, v]) => ({ key: k, value: v }));
    return kvpair.map((item) => (
      <Chip
        key={item.key}
        label={item.value}
        onDelete={(evt) => deleteSearchChip(evt, item.key)}
        color='secondary'
        size='small'
      />
    ));
  };

  const deleteSearchChip = (evt, key) => {
    const sp = { ...searchParameters };
    delete sp[key];
    executeSearch(sp);
  };

  /* Equipment and Truck CRUD */
  const saveEquipment = (equipment) => {
    dispatch(EquipmentAction.save(equipment));
  };

  const updateEquipment = (equipment) => {
    dispatch(EquipmentAction.update(equipment));
  };

  const saveTruck = (truck) => {
    dispatch(TruckAction.saveTruck(truck));
  };

  const updateTruck = (truck) => {
    dispatch(TruckAction.updateTruck(truck));
  };

  const updateTruckMaintenanceRecord = (maintenanceRecord) => {
    const truckRecord = { ...selectedTruck };
    truckRecord.maintenanceRecordList = Array(maintenanceRecord);
    dispatch(TruckAction.updateTruck(truckRecord));
  };

  const editMaintenanceRecord = (record) => {
    setMaintenanceRecord(record);
    setAction('Update');
  }

  const deleteMaintenanceRecord = (record) => {
    const truck = selectedTruck;
    const records = truck.maintenanceRecordList.filter(rec => rec.id !== record.id);
    truck.maintenanceRecordList = records;
    dispatch(TruckAction.updateTruck(truck));
  }

  /* Dialog toggles */
  const toggleEquipmentFormDialog = () => {
    setIsEquipmentFormDrawerOpen((prev) => !prev);
    setIsFormDialogOpen(false);
  };

  const toggleTruckFormDialog = () => {
    setIsTruckFormDrawerOpen((prev) => !prev);
    setIsFormDialogOpen(false);
  };

  const closeDialog = () => {
    dispatch(EquipmentAction.resetState('RESET_TOAST_STATE'));
    setEquipment(formState.equipment);
    setTruck(formState.truck);
    setIsEquipmentFormDrawerOpen(false);
    setIsTruckFormDrawerOpen(false);
    setIsFileLoaderDialogOpen(false);
    setIsMaintenanceDialogOpen(false);
    setAnchorEl(null);
    setSelectedRow(null);
    setAction('Save');
  };

  /* Edit handlers */
  const editEquipment = (row) => {
    setIsEquipmentFormDrawerOpen(true);
    setAction('Update');
    setEquipment({ ...row, ...row.address });
  };

  const editTruck = (row) => {
    const truckType = row.truckType === 'LIVESTOCK' ? 'TRACTOR' : row.truckType; // TEMP
    setIsTruckFormDrawerOpen(true);
    setAction('Update');
    setTruck({ ...row, truckType, ...row.address });
  };

  const addMaintenanceInformation = (row) => {
    setIsMaintenanceDialogOpen(true);
    setAction('Save');
    setMaintenanceList(row.maintenanceRecordList || []);
    setSelectedTruck(row);
    setMaintenanceRecord({
      maintenanceId: '',
      date: '',
      description: '',
      amount: '',
      serviceCenter: '',
      mileage: '',
      address: '',
      volume: '',
      category: '',
    });
  };

  /* Tab Change */
  const changeTab = (index) => {
    setTabIndex(index);
  };

  /* Menu Handling */
  const handleMenuClick = (evt, data) => {
    setAnchorEl(evt.currentTarget);
    setSelectedRow(data);
  };

  /* Download functions - unchanged, but converted to arrow functions */
  const downloadTemplate = () => {
    const assetType = TABS[tabIndex];
    const filename = 'equipment_template.csv';
    const headers = [];
    if (assetType === 'TRUCK') {
      headers.push(['Unit Number', 'DOT', 'Type', 'License Plate', 'Registration Date', 'Insurance', 'Insurance Exp.Date', 'Make', 'VIN', 'Miles']);
    } else if (assetType === 'CONTAINER') {
      headers.push(['Container Line', 'Free Days', 'Drop Location']);
    } else {
      headers.push(['Chassis Company', 'Rent']);
    }
    let csvContent = 'data:text/csv;charset=utf-8,' + headers;
    const data = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', data);
    link.setAttribute('download', filename);
    link.click();
  };

  const downloadEquipments = () => {
    const filename = 'equipments.csv';
    const assetType = TABS[tabIndex];
    const csvData = [];
    const headers = [];
    if (assetType === 'TRUCK') {
      headers.push(['Unit Number', 'DOT', 'Type', 'License Plate', 'Registration Date', 'Insurance', 'Insurance Exp.Date', 'Make', 'VIN', 'Miles']);
    } else if (assetType === 'CONTAINER') {
      headers.push(['Container Line', 'Free Days', 'Drop Location']);
    } else {
      headers.push(['Chassis Company', 'Rent']);
    }
    csvData.push(headers);
    if (assetType === 'TRUCK') {
      trucks.forEach(item => {
        let arr = [item.id, item.dotNumber, item.truckType, item.licensePlate, item.registrationExpirationDate, item.insuranceNumber, item.insuranceExpirationDate, item.make, item.vinNumber, item.totalMilesDriven];
        csvData.push(arr);
      });
    } else if (assetType === 'CONTAINER') {
      equipments.forEach(item => {
        let arr = [item.name, item.freeDays];
        csvData.push(arr);
      });
    } else {
      equipments.forEach(item => {
        let arr = [item.name, item.rate];
        csvData.push(arr);
      });
    }
    let csvContent = 'data:text/csv;charset=utf-8,';
    csvData.forEach(function (rowArray) {
      let row = rowArray.join(',');
      csvContent += row + '\r\n';
    });
    const data = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', data);
    link.setAttribute('download', filename);
    link.click();
  };

  /* Save Form exposed by child */
  const saveForm = (formData) => {
    if (action === 'Save') saveEquipment(formData);
    else updateEquipment(formData);
  };

  const updateForm = (formData) => {
    updateEquipment(formData);
  };

  /* SaveTruck / UpdateTruck passed to TruckForm */
  // These are already implemented as saveTruck, updateTruck above

  /* submitForm handler setter from child */
  const exposeSubmit = (submitFn) => {
    submitFormRef.current = submitFn;
  };

  /* Trigger submitForm */
  const onSubmitFormClick = () => {
    if (submitFormRef.current) submitFormRef.current();
  };

  // Render logic similar to original

  const isSearchOn = isSearchEnabled;
  const selectedTab = TABS[tabIndex];

  // Equipment filtered by type
  const equipmentData = isSearchOn ? searched : equipments;
  const equipmentDataByStatus = equipmentData
    ? equipmentData.filter((d) => {
        if (!d.type) return d;
        return d.type.toUpperCase() === selectedTab;
      })
    : [];

  const truckData = isSearchOn ? searchedTrucks : trucks;

  const statusCounts = [];
  if (equipmentData) {
    statusCounts.push(
      {
        status: 'CONTAINER',
        count: equipmentData.filter((d) => d.type.toUpperCase() === 'CONTAINER').length,
      },
      {
        status: 'CHASSIS',
        count: equipmentData.filter((d) => d.type.toUpperCase() === 'CHASSIS').length,
      }
    );
  }
  if (truckData) {
    statusCounts.push({
      status: 'VEHICLE',
      count: truckData.length,
    });
  }

  return (
    <Page title='Equipments'>
      <Box display={'flex'} justifyContent={'space-between'}>
        <PageHeader title='EQUIPMENT' subTitle='Manage Your Equipments' />
        <Searchbar
          searchType='SIMPLE'
          searchKey='name'
          searchParameters={searchParameters}
          fnOnSearchHandler={executeSearch}
        />
        <Notificationbar />
      </Box>
      <Box m='20px 0 0 0' height='75vh'>
        <Box style={{ margin: '10px 0' }}>
          <Box display={'flex'} justifyContent={'space-between'}>
            <Button variant='contained' color='primary' onClick={() => setIsFormDialogOpen(true)}>
              {'New Equipment'}
            </Button>
            {isProcessing && <CircularProgress style={{ margin: 'auto' }} />}
            {toast ? <ToastHandler toast={toast} type={toast.type} /> : null}
            <ButtonGroup variant='outlined' color='secondary'>
              <Button onClick={() => setIsFileLoaderDialogOpen(true)}>Import</Button>
              <Button onClick={downloadTemplate}>Template</Button>
              <Button onClick={downloadEquipments}>Export</Button>
            </ButtonGroup>
          </Box>
        </Box>
        <Box display={'flex'}>
          <Box flex={1}>
            <CTTabs labels={TABS} counts={statusCounts} tabIndex={tabIndex} fnOnTabChangeHandler={changeTab} />
          </Box>
          {isSearchOn && (
            <Box flex={1}>
              <Typography
                variant='h5'
                style={{
                  letterSpacing: 1,
                  color: '#667085',
                  textTransform: 'uppercase',
                  marginBottom: '2px',
                }}
              >
                {`${searched.length} records found`}
              </Typography>
              {createSearchChip()}
            </Box>
          )}
        </Box>
        {tabIndex === 0 ? (
          <DataGrid rows={truckData} columns={truckHeaders} rowHeight={40} disableSelectionOnClick />
        ) : (
          <DataGrid rows={equipmentDataByStatus} columns={columns} rowHeight={40} disableSelectionOnClick />
        )}
      </Box>

      {/* Equipment Form Drawer */}
      <FormDrawer
        open={isEquipmentFormDrawerOpen}
        title={`${action === 'Save' ? 'Create Equipment' : 'Edit Equipment'}`}
        toast={toast}
        isProcessing={isProcessing}
        handleClose={closeDialog}
        footer={
          <FormElements.FormFooter>
            <ButtonCancel action={closeDialog} />
            <ButtonSave title={action} action={onSubmitFormClick} />
          </FormElements.FormFooter>
        }
      >
        <EquipmentForm
          action={action}
          equipment={equipment}
          saveFormHandler={saveForm}
          updateFormHandler={updateForm}
          exposeSubmit={exposeSubmit}
        />
      </FormDrawer>

      {/* Truck Form Drawer */}
      <FormDrawer
        open={isTruckFormDrawerOpen}
        title={`${action === 'Save' ? 'Create Truck' : 'Edit Truck'}`}
        toast={toast}
        isProcessing={isLoading}
        handleClose={closeDialog}
        footer={
          <FormElements.FormFooter>
            <ButtonCancel action={closeDialog} />
            <ButtonSave title={action} action={onSubmitFormClick} />
          </FormElements.FormFooter>
        }
      >
        <TruckForm action={action} truck={truck} saveFormHandler={saveTruck} updateFormHandler={updateTruck} exposeSubmit={exposeSubmit} />
      </FormDrawer>

      {/* Select Equipment Dialog */}
      <DataDialog open={isFormDialogOpen} title='Select Equipment' width='sm' onCloseHandler={() => setIsFormDialogOpen(false)}>
        <Box display='flex' justifyContent='space-between'>
          <Button variant='outlined' onClick={toggleTruckFormDialog} sx={{p:2, m: 1}}>
            <Typography variant='subtitle1'>Add Truck or Trailer</Typography>
          </Button>

          <Button variant='outlined' onClick={toggleEquipmentFormDialog} sx={{p:2, m: 1}}>
            <Typography variant='subtitle1'>Add Container or Chassis</Typography>
          </Button>
        </Box>
      </DataDialog>

      {/* Maintenance Dialog */}
      <ContentDrawer 
        open={isMaintenanceDialogOpen}
        title='Maintenance Record' 
        toast={truckToast} 
        isProcessing={isLoading} 
        handleClose={closeDialog}
      >
        <MaintenanceForm
          action={action}
          maintenanceRecord={maintenanceRecord}
          maintenanceList={maintenanceList}
          saveFormHandler={updateTruckMaintenanceRecord}
          updateFormHandler={updateTruckMaintenanceRecord}
          fnOnEditHandler={editMaintenanceRecord}
          fnOnDeleteHandler={deleteMaintenanceRecord}
        />
      </ContentDrawer>

      {/* File Loader Dialog */}
      <DataDialog open={isFileLoaderDialogOpen} title='Import' toast={toast} isProcessing={isProcessing} onCloseHandler={closeDialog}>
        <FileUploader name={'equipment'} />
      </DataDialog>
    </Page>
  );
};

export default EquipmentPage;